import React from 'react';
import {
  Badge, Box, Button, HStack, Table, Tbody, Td, Text, Th, Thead, Tr,
} from '@chakra-ui/react';
import { FiClock } from 'react-icons/fi';
import { useHistory } from 'react-router-dom';
import { useQuery } from 'react-query';
import axios from 'axios';
import FormRow from './forms/FormRow';

const apiUrl = () => process.env.REACT_APP_BASIX_API || '';

// item 1343 (Chad) — the bid job's INTERNAL costing panel: actual per-day time every person
// tracked onto this work order (proposal-backed WOs take timer bookings as tracking, never
// billing). Admins also see the labor-cost view (hours × payroll HourlyRate). Never prints,
// never copies to the invoice. Renders nothing when no time was tracked.
interface TrackedRow { date: string; userName: string; note?: string | null; seconds: number; cost?: number | null }
interface TrackedPayload { rows: TrackedRow[]; totalSeconds: number; laborCost?: number | null }

const hm = (seconds: number): string => `${Math.floor(seconds / 3600)}h ${String(Math.floor((seconds % 3600) / 60)).padStart(2, '0')}m`;
const money = (n: number): string => n.toLocaleString('en-US', { style: 'currency', currency: 'USD' });

const WorkOrderTrackedTime: React.FC<{ workOrderId: string }> = ({ workOrderId }) => {
  const history = useHistory();
  const { data } = useQuery<TrackedPayload>(['timeClockWo', workOrderId], async () => {
    const { data: res } = await axios.get(`${apiUrl()}TimeClock/work-order/${workOrderId}`);
    return res;
  }, { enabled: !!workOrderId });

  if (!data) return null; // still loading — don't flash an empty section
  const rows = data.rows ?? [];
  return (
    <FormRow
      heading="Tracked Time"
      subHeading="Internal job costing — actual hours tracked by the timer on this bid job. Never prints."
    >
      {/* item 1347 (Chad: "add a hyperlink to work orders to go to timer") — the WO's jump
          into the timer home; renders whether or not time exists yet */}
      <Button
        size="sm"
        variant="outline"
        leftIcon={<FiClock />}
        mb={rows.length > 0 ? 3 : 0}
        onClick={() => history.push('/my-time')}
      >
        Open the timer (My Time)
      </Button>
      {rows.length === 0 && (
        <Text fontSize="sm" opacity={0.6} mt={2}>
          No time tracked yet — start a timer and book it to this work order.
        </Text>
      )}
      {rows.length > 0 && (
      <>
        <Box overflowX="auto">
          <Table size="sm">
            <Thead>
              <Tr>
                <Th>Date</Th>
                <Th>Who</Th>
                <Th>Note</Th>
                <Th isNumeric>Actual</Th>
                {data.laborCost != null && <Th isNumeric>Cost</Th>}
              </Tr>
            </Thead>
            <Tbody>
              {rows.map((r, i) => (
              // eslint-disable-next-line react/no-array-index-key
                <Tr key={`${r.date}-${r.userName}-${i}`}>
                  <Td whiteSpace="nowrap">{new Date(`${r.date}T12:00:00`).toLocaleDateString([], { month: 'numeric', day: 'numeric', year: 'numeric' })}</Td>
                  <Td whiteSpace="nowrap">{r.userName}</Td>
                  <Td><Text noOfLines={1}>{r.note || '—'}</Text></Td>
                  <Td isNumeric fontFamily="mono">{hm(r.seconds)}</Td>
                  {data.laborCost != null && <Td isNumeric>{r.cost != null ? money(r.cost) : '—'}</Td>}
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>
        <HStack mt={2} spacing={3}>
          <Badge colorScheme="purple" variant="subtle">{`${hm(data.totalSeconds)} tracked`}</Badge>
          {data.laborCost != null && (
          <Text fontSize="sm" opacity={0.75}>{`≈ ${money(data.laborCost)} labor (payroll rates)`}</Text>
          )}
        </HStack>
      </>
      )}
    </FormRow>
  );
};

export default WorkOrderTrackedTime;
